'use client';

import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import _ from 'lodash';
import { extendedAreaLoader } from '@/components/Filters/Area/Extended/Store/utils';
import { getAreaModeDescription } from '@/components/Filters/Area/Extended/utils/stringUtils';
import { getConstructionYearDescription } from '@/components/Filters/ConstructionYear/utils/stringUtils';
import { ALL_DEVELOPERS } from '@/components/Filters/Developer/modules/Popover';
import { getNewConstructionDescription } from '@/components/Filters/IsNewConstruction/utils/stringUtils';
import { getObjectTypeDescription } from '@/components/Filters/ObjectType/utils/stringUtils';
import { periodLoader } from '@/components/Filters/Period/store/utils';
import { getPeriodDescription } from '@/components/Filters/Period/utils/stringUtils';
import type { PhaseTypes } from '@/components/Filters/Phase/Store/utils';
import { getProjectPhaseDescription } from '@/components/Filters/Phase/utils/stringUtils';
import { getRoomSizeDescription } from '@/components/Filters/Room/utils/stringUtils';
import { getSizeDescription } from '@/components/Filters/Size/utils/stringUtils';
import { getTenureDescription } from '@/components/Filters/Tenure';
import type { Area } from '@/types/booliApi';
import type { Agency } from '@/types/hittamaklareAPI';
import type { Source } from '@/types/nestjs';
import type { StatisticsSeries } from '@/types/series';
import type { StatisticsData } from '@/types/statisticsApi';
import type { SeriesDefinition } from '@/types/statisticsSeriesDefs';
import { createDynamicColumns } from '../../../components/Table/utils/CreateDynamicColumns';
import type { Compare, QueryProps } from '../types';
import { checkDifference, exportChartToExcel, exportTableToExcel, formatToChart, formatToTable } from '../utils/index';
import { StatisticsQuery } from './BaseQuery';

type FilterToInclude =
  | keyof StatisticsQueryClient<StatisticsSeries, Compare>
  | 'areaMode'
  | 'period'
  | 'phase'
  | 'developer';

const EXCLUDE = ['result', 'comparisonQueries', 'areaData', 'developerData', 'agencyData', 'seriesData'];

export const RESOURCE_TO_TITLE = {
  areas: 'Bostadsmarknad',
  developers: 'Bostadsutvecklare',
  agencies: 'Mäklarbyråer',
  agents: 'Mäklare',
  projects: 'Bostadsprojekt',
  'housing-coops': 'BRF',
};

class StatisticsQueryClient<Series extends StatisticsSeries, C extends Compare>
  extends StatisticsQuery<Series, C>
  implements QueryProps<Series, C>
{
  searchParams: string;
  comparisonQueries: StatisticsQueryClient<Series, C>[] | undefined;
  useBaseQuery!: (
    options?: Partial<Omit<UseQueryOptions<StatisticsData<Series, C>>, 'queryKey' | 'queryFn'>>,
  ) => UseQueryResult<StatisticsData<Series, C>, Error>;
  useComparisonQueries!: () => UseQueryResult<StatisticsData<Series, C>, Error>[];
  areaData: Area[] = [];
  developerData: Source[] = [];
  agencyData: Agency[] = [];
  seriesData: SeriesDefinition<Series>[] = [];

  constructor(
    props: ConstructorParameters<typeof StatisticsQuery<Series, C>>[0] & {
      searchParams: string;
      areaData: Area[];
      developerData: Source[];
      agencyData: Agency[];
      seriesData: SeriesDefinition<Series>[];
    },
  ) {
    super(props);

    this.searchParams = props.searchParams;
    this.areaData = props.areaData;
    this.developerData = props.developerData;
    this.agencyData = props.agencyData;
    this.seriesData = this.series.map((slug) => props.seriesData.find((s) => s.slug === slug)).filter((s) => !!s);
  }

  addComparisonQuery(comparison: Partial<QueryProps<Series, C>>[]) {
    comparison = comparison.map((c) => Object.fromEntries(Object.entries(c).filter(([_, v]) => v !== undefined)));
    const queries = comparison.map((compareQuery) => {
      return new StatisticsQueryClient({
        ...this,
        ...compareQuery,
      });
    });

    this.comparisonQueries = queries;
  }

  async fetchComparison() {
    const comparisonQueries = this.comparisonQueries ?? [];
    const promise = await Promise.all(comparisonQueries.map((c) => c.fetchHandler()));

    const data = promise.map((p, i) => {
      comparisonQueries[i].result = p;

      return p;
    });

    return data;
  }

  async fetchAll() {
    await this.fetchBase();
    await this.fetchComparison();

    return this;
  }

  getLabel(filtersToInclude: FilterToInclude[]) {
    const parts = [];

    if (filtersToInclude.includes('ids')) {
      const developers = this.ids?.map((id) => this.developerData.find((developer) => developer.id === id)?.name);
      const areas = this.ids?.map((id) => this.areaData.find((area) => area.id === id)?.name);
      // Agent names come from the statistics rows; the full agents list is not fetched (like the old Ember app)
      const agents = this.ids?.map((id) => this.result?.rows.find((row) => row.id === id)?.name);
      const agencies = this.ids?.map((id) => this.agencyData.find((agency) => agency.id === id)?.name);

      if (this.resource === 'developers') {
        parts.push(developers?.filter(Boolean).join(', ') ?? '');
      }
      if (this.resource === 'areas') {
        parts.push(areas?.filter(Boolean).join(', ') ?? '');
      }
      if (this.resource === 'agents') {
        parts.push(agents?.filter(Boolean).join(', ') ?? '');
      }
      if (this.resource === 'agencies') {
        parts.push(agencies?.filter(Boolean).join(', ') ?? '');
      }
    }

    if (filtersToInclude.includes('areaIds')) {
      const areas = this.areaIds?.map((id) => this.areaData.find((area) => id === area.id)?.name);
      parts.push(areas?.filter(Boolean).join(' & ') ?? '');
    }

    if (filtersToInclude.includes('areaMode')) {
      const sp = this.searchParams;
      const areas = extendedAreaLoader(sp);
      parts.push(getAreaModeDescription(areas.areaMode, areas.areaParent, this.areaData, areas.areaIds));
    }

    if (filtersToInclude.includes('period')) {
      const sp = this.searchParams;
      const period = periodLoader({ periodSlug: this.periodSlug, compareSlug: this.compareSlug })(sp);
      parts.push(getPeriodDescription(period));
    }

    if (filtersToInclude.includes('isNewConstruction')) {
      parts.push(getNewConstructionDescription(this.isNewConstruction));
    }

    if (filtersToInclude.includes('objectTypes')) {
      parts.push(getObjectTypeDescription(this.objectTypes));
    }

    if (filtersToInclude.includes('tenure')) {
      parts.push(getTenureDescription(this.tenure)?.name);
    }

    if (filtersToInclude.includes('size')) {
      parts.push(getSizeDescription(this.size));
    }

    if (filtersToInclude.includes('minConstructionYear') || filtersToInclude.includes('maxConstructionYear')) {
      parts.push(getConstructionYearDescription(this.maxConstructionYear, this.minConstructionYear));
    }

    if (filtersToInclude.includes('rooms')) {
      parts.push(getRoomSizeDescription(this.rooms));
    }

    if (filtersToInclude.includes('phase')) {
      if (this.projectPhases?.length) {
        parts.push(getProjectPhaseDescription(this.projectPhases as PhaseTypes[]));
      }
    }

    if (filtersToInclude.includes('developer')) {
      if (this.developerIds?.length) {
        const developerNames = this.developerIds
          .map((id) => this.developerData.find((dev) => dev.id === id)?.name)
          .filter(Boolean);
        if (developerNames.length) {
          parts.push(developerNames.join(', '));
        }
      } else {
        parts.push(ALL_DEVELOPERS);
      }
    }

    return parts.join(' • ');
  }

  formatToChart() {
    const baseResult = this.result;
    const combinedData = [baseResult, ...(this.comparisonQueries?.map((d) => d.result) || [])];

    if (!baseResult || !combinedData.every((data) => !!data)) {
      return;
    }

    // checkDifference returns the differing property names; they are keys of
    // this query, i.e. valid FilterToInclude values.
    const filterDiff = [
      ...new Set(
        this.comparisonQueries?.flatMap((query) =>
          checkDifference({
            query1: this,
            query2: query,
            exclude: EXCLUDE,
          }),
        ),
      ),
    ] as FilterToInclude[];

    const baseData = formatToChart(baseResult, this.series, this.getLabel(filterDiff));

    const comparisonData = (this.comparisonQueries || []).flatMap((d) => {
      const result = d.result;
      if (!result) return [];
      return formatToChart(result, this.series, d.getLabel(filterDiff));
    });

    return [baseData, ...comparisonData];
  }

  formatToTable() {
    const data = this.result;

    if (!data) {
      return;
    }

    return formatToTable(data, this.resource);
  }

  getDynamicTableColumns() {
    const selectedSeries = this.seriesData;
    const formattedTable = this.formatToTable();

    return createDynamicColumns(selectedSeries, formattedTable, !!this.compare);
  }

  exportChartQueryToExcel(filtersToInclude: FilterToInclude[]) {
    const selectedSeries = this.seriesData;

    const exportTitle = RESOURCE_TO_TITLE[this.resource];
    const baseData = this.result;
    const comparisonData = this.comparisonQueries?.map((d) => d.result);
    const combinedData = [baseData, ...(comparisonData || [])];

    if (!combinedData?.every((data) => !!data)) {
      console.log('No data to be exported');
      return;
    }

    const queryFilterLabels = this.getLabel(filtersToInclude);

    const comparisonQueryDiff = [
      ...new Set(
        this.comparisonQueries?.flatMap((query) =>
          checkDifference({
            query1: this,
            query2: query,
            exclude: EXCLUDE,
          }),
        ),
      ),
    ] as FilterToInclude[];

    const comparisonFilterLabels = [
      this.getLabel(comparisonQueryDiff),
      ...(this.comparisonQueries ? this.comparisonQueries.map((query) => query.getLabel(comparisonQueryDiff)) : []),
    ];

    return exportChartToExcel(combinedData, selectedSeries, exportTitle, queryFilterLabels, comparisonFilterLabels);
  }

  exportTableQueryToExcel(filtersToInclude: FilterToInclude[]) {
    const selectedSeries = this.seriesData;
    const exportTitle = RESOURCE_TO_TITLE[this.resource];
    const tableData = this.formatToTable();

    if (!tableData) {
      console.log('No data to be exported');
      return;
    }

    const queryFilterLabel = this.getLabel(filtersToInclude);

    return exportTableToExcel(tableData, selectedSeries, exportTitle, queryFilterLabel);
  }

  clone() {
    return _.cloneDeep(this);
  }
}

export { type FilterToInclude, StatisticsQueryClient };
